import { useApiClient } from "@/modules/api/apiClient.store.ts";
import type { PaymentParams, PaymentProps } from "@/modules/billing/payment/PaymentParams";
import {
    getRedirectUrlFromData,
    linkSendData,
    paymentResultRequest,
    redirectToGatewayAndWaitForResponse,
} from "@/modules/billing/payment/paymentUtils.ts";
import { parsePaypalCheckoutPaymentResult } from "@/modules/billing/paypal/parsePaypalCheckoutPaymentResult.tsx";
import { t_StartingPayment } from "@/modules/billing/progressLabels.tsx";
import { useUnmountSignal } from "@/utils/react/useUnmountSignal.tsx";
import type {
    MethodConfig,
    PaypalCheckoutRedirectSendDataResponse,
    ProcessorConfig,
} from "@warrenio/api-spec/spec.oats.gen";
import { useImperativeHandle } from "react";

export type PaypalCheckoutProcessorConfig = ProcessorConfig & { type: "link"; keyword: "paypal_checkout" };

export default function PaypalCheckoutPaymentForm({
    actionsRef,
    method: { key },
}: {
    config: PaypalCheckoutProcessorConfig;
    method: MethodConfig;
} & PaymentProps) {
    //#region Hooks

    const apiClient = useApiClient();
    const signal = useUnmountSignal();

    useImperativeHandle(actionsRef, () => ({
        needsPopUp(_params) {
            return true;
        },

        async makePayment(params: PaymentParams) {
            const { progress, popup, account } = params;

            progress(t_StartingPayment);
            const { data } = await linkSendData<PaypalCheckoutRedirectSendDataResponse>(
                apiClient,
                params,
                "paypal_checkout",
                { paymentMethod: key },
            );

            const { persistent_data_ref } = data;
            return await redirectToGatewayAndWaitForResponse({
                popup,
                progress,
                redirectUrl: getRedirectUrlFromData(data),
                signal,
                check: async () =>
                    parsePaypalCheckoutPaymentResult(
                        await paymentResultRequest(apiClient, account.id, persistent_data_ref, signal),
                    ) != null,
            });
        },
    }));
    //#endregion

    return null;
}
